import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseIncompletePhoneNumber } from 'libphonenumber-js/mobile'
import { formatIncompletePhoneNumber } from 'libphonenumber-js'
import Input from '../../Input'
import { RegField } from '../types'
import { useGeoRuntime } from '../../../geo/useGeoRuntime'
import { DEFAULT_GEO, GEO_CONFIG, GEO_META, type SupportedGeo } from '../../../geo/config'

interface PhoneInputProps {
  phoneNumber?: string
  error?: string | React.ReactElement
  disabled?: boolean
  selectedGeo?: SupportedGeo
  onChange: <K extends keyof RegField>(key: K, value: RegField[K]) => void
  onValidate: (...keys: ['clean_phone']) => () => void
  onGeoChange?: (geo: SupportedGeo) => void
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  phoneNumber,
  error,
  disabled,
  selectedGeo,
  onChange,
  onValidate,
  onGeoChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const { allowedRegions } = useGeoRuntime()
  const displayGeo = selectedGeo ?? DEFAULT_GEO
  const displayPhone = GEO_CONFIG[displayGeo].phone
  const hasMultipleGeoOptions = allowedRegions.length > 1
  const valuePhone = useMemo(
    () =>
      phoneNumber
        ? formatIncompletePhoneNumber(phoneNumber, {
            defaultCountry: displayPhone.code,
            defaultCallingCode: displayPhone.dial,
          })
        : phoneNumber,
    [displayPhone.code, displayPhone.dial, phoneNumber],
  )

  const handleSetDefaultValue = useCallback(() => {
    if (valuePhone && valuePhone !== '+') return
    onChange('clean_phone', `+${displayPhone.dial}`) // ← ставим код при фокусе
  }, [onChange, displayPhone.dial, valuePhone])

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = e.target
      const digitsOnly = value.replace(/\D/g, '')
      const dial = displayPhone.dial
      if (!digitsOnly || digitsOnly === dial || digitsOnly.length <= dial.length) {
        onChange('clean_phone', `+${dial}`)
        onValidate('clean_phone')()
        return
      }
      const nationalNumber = digitsOnly.startsWith(dial)
        ? digitsOnly.slice(dial.length)
        : digitsOnly

      const phoneNumberStr = `+${dial}${nationalNumber}`
      if (phoneNumberStr.length > displayPhone.maxLength) return

      onChange('clean_phone', parseIncompletePhoneNumber(phoneNumberStr))
      onValidate('clean_phone')()
    },
    [onChange, onValidate, displayPhone.dial, displayPhone.maxLength],
  )
  const handleBlur = useCallback(() => onValidate('clean_phone')(), [onValidate])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!hasMultipleGeoOptions) {
      setIsOpen(false)
    }
  }, [hasMultipleGeoOptions])

  const handleGeoSelect = useCallback(
    (nextGeo: SupportedGeo) => {
      if (nextGeo === displayGeo) {
        setIsOpen(false)
        return
      }
      setIsOpen(false)
      onGeoChange?.(nextGeo)
      onChange('clean_phone', '')
      onValidate('clean_phone')()
    },
    [displayGeo, onChange, onGeoChange, onValidate],
  )
  return (
    <Input
      type="tel"
      disabled={disabled}
      placeholder={displayPhone.placeholder}
      className="input"
      value={valuePhone}
      errorMsg={error}
      inputMode="numeric"
      onFocus={handleSetDefaultValue}
      onBlur={handleBlur}
      onChange={handlePhoneChange}
      prefixIcon={
        <div ref={pickerRef} className="phonePrefix">
          <button
            type="button"
            className={`phonePrefix__button${isOpen ? ' active' : ''}`}
            onClick={() => {
              if (hasMultipleGeoOptions) {
                setIsOpen((prev) => !prev)
              }
            }}
            aria-disabled={!hasMultipleGeoOptions}
            aria-expanded={hasMultipleGeoOptions ? isOpen : undefined}
            aria-label="Select phone geo"
          >
            <img src={displayPhone.flagSrc} className="phoneIcon" alt={displayPhone.flagAlt} />
            {hasMultipleGeoOptions ? <span className="phonePrefix__caret" /> : null}
          </button>
          {hasMultipleGeoOptions && isOpen ? (
            <div className="phonePrefix__menu">
              {allowedRegions.map((optionGeo) => (
                <button
                  key={optionGeo}
                  type="button"
                  className={`phonePrefix__option${optionGeo === displayGeo ? ' active' : ''}`}
                  onClick={() => handleGeoSelect(optionGeo)}
                >
                  <img
                    src={GEO_META[optionGeo].flag}
                    alt={GEO_META[optionGeo].label.toLowerCase()}
                  />
                  <span>{GEO_META[optionGeo].label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      }
    />
  )
}

export default memo(PhoneInput)
